import { colors, radii, shadows, FONT, SERIF } from '../../theme';

// ═══════════════════════════════════════════════════════════════
//  Main Dashboard Styles (S)
// ═══════════════════════════════════════════════════════════════

export const S = {
  container: { minHeight: '100vh', background: 'transparent', fontFamily: FONT, color: colors.gray900 },

  // Header
  header: { paddingTop: 'max(16px, env(safe-area-inset-top))', paddingLeft: 20, paddingRight: 20, paddingBottom: 4, background: 'rgba(21,20,26,0.85)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: `1px solid ${colors.gray200}` },
  headerTop: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 },
  headerUser: { fontSize: 13, color: colors.gray400, fontWeight: 500 },
  headerTitle: { fontSize: 28, fontWeight: 600, color: colors.gray900, letterSpacing: 0, fontFamily: SERIF },

  // Update banner
  updateBanner: {
    position: 'fixed', top: 'max(12px, env(safe-area-inset-top))', left: '50%', transform: 'translateX(-50%)',
    background: colors.gray900, color: colors.white, padding: '10px 20px', borderRadius: radii.full,
    fontSize: 13, fontWeight: 600, zIndex: 90, cursor: 'pointer',
    boxShadow: shadows.xl, whiteSpace: 'nowrap',
    animation: 'slideDown 0.4s ease-out',
  },

  // Pull-to-refresh
  pullIndicator: {
    position: 'fixed', top: 'max(60px, calc(env(safe-area-inset-top) + 60px))', left: '50%',
    marginLeft: -20, width: 40, height: 40, zIndex: 80,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'opacity 0.2s',
  },
  pullSpinner: {
    width: 36, height: 36, borderRadius: '50%', background: colors.white,
    boxShadow: shadows.md, display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontSize: 18, color: colors.gray900, fontWeight: 700,
  },

  // Main
  main: { padding: '12px 20px', paddingBottom: 'calc(80px + env(safe-area-inset-bottom))' },

  // Cards
  cardList: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: {
    background: colors.white, borderRadius: radii.lg, padding: '14px 16px',
    border: `1px solid ${colors.gray200}`, transition: 'all 0.3s ease',
  },
  cardGlow: {
    border: '1.5px solid rgba(79,182,140,0.4)',
    animation: 'siriGlow 2s ease-in-out infinite',
    background: `linear-gradient(135deg, rgba(79,182,140,0.03) 0%, ${colors.white} 100%)`,
  },
  cardActive: {
    border: '1.5px solid rgba(79,182,140,0.3)',
    background: `linear-gradient(135deg, rgba(79,182,140,0.02) 0%, ${colors.white} 100%)`,
  },
  cardTop: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 },
  cardDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  cardTitle: { fontSize: 15, fontWeight: 600, color: colors.gray900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardMeta: { fontSize: 12, color: colors.gray400, flexShrink: 0, fontWeight: 500 },
  cardUrl: { fontSize: 11, color: colors.gray400, marginBottom: 8, wordBreak: 'break-all', fontFamily: 'monospace' },
  cardActions: { display: 'flex', gap: 6, borderTop: `1px solid ${colors.gray200}`, paddingTop: 10 },
  cardBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: colors.gray50, border: `1px solid ${colors.gray200}`, color: colors.gray600,
    fontSize: 12, padding: '5px 10px', cursor: 'pointer', borderRadius: radii.sm, fontFamily: FONT,
    transition: 'all 0.15s',
  },
  cardBtnActive: { borderColor: colors.gray900, color: colors.gray900, background: colors.gray100 },
  cardCallActions: {
    display: 'flex', gap: 8, marginTop: 8,
  },
  answerBtn: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    background: colors.greenDot, border: 'none', color: colors.white,
    padding: '10px 16px', borderRadius: radii.sm, fontSize: 14, fontWeight: 600,
    cursor: 'pointer', fontFamily: FONT,
  },
  declineBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: colors.gray50, border: `1px solid ${colors.gray200}`, color: colors.gray600,
    padding: '10px 16px', borderRadius: radii.sm, fontSize: 14, fontWeight: 500,
    cursor: 'pointer', fontFamily: FONT,
  },

  // FAB
  fab: { position: 'fixed', bottom: 'calc(80px + env(safe-area-inset-bottom) + 16px)', right: 20, width: 52, height: 52, borderRadius: '50%', background: colors.gray900, color: colors.white, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: shadows.lg, zIndex: 40 },

  // Error
  errorBanner: { background: colors.redLight, border: `1px solid ${colors.red}33`, color: colors.red, fontSize: 13, padding: '10px 14px', borderRadius: radii.md, marginBottom: 12 },

  // History
  dateLabel: { fontSize: 11, fontWeight: 600, color: colors.gray500, padding: '16px 0 8px', textTransform: 'uppercase', letterSpacing: 2 },
  historyRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: colors.white, borderRadius: radii.md, marginBottom: 2, border: `1px solid ${colors.gray200}` },
  historyName: { fontSize: 14, fontWeight: 500, color: colors.gray900, marginBottom: 2 },
  historySub: { fontSize: 12, color: colors.gray400 },

  // Contacts (compact Telegram-style)
  searchWrap: { position: 'relative', marginBottom: 12 },
  searchIcon: { position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 14, pointerEvents: 'none', display: 'flex' },
  searchInput: { width: '100%', background: colors.gray100, border: 'none', borderRadius: radii.full, padding: '10px 14px 10px 38px', fontSize: 15, fontFamily: FONT, color: colors.gray900, outline: 'none' },
  sectionLabel: { fontSize: 11, fontWeight: 600, color: colors.gray500, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8, marginTop: 8, paddingLeft: 4 },
  compactRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '8px 4px', cursor: 'pointer',
    borderBottom: `1px solid ${colors.gray100}`,
  },
  compactAvatar: {
    width: 36, height: 36, borderRadius: '50%',
    background: colors.gray100, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 600, color: colors.gray600,
    flexShrink: 0,
  },
  onlineDot: {
    position: 'absolute', bottom: 0, right: 0,
    width: 10, height: 10, borderRadius: '50%',
    background: colors.greenDot, border: `2px solid ${colors.white}`,
  },
  compactName: { fontSize: 14, fontWeight: 500, color: colors.gray900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  compactSub: { fontSize: 12, color: colors.gray400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 },
  compactIconBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 6, display: 'flex', alignItems: 'center' },
  chatPreviewRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 4px', cursor: 'pointer',
    borderBottom: `1px solid ${colors.gray100}`,
  },
  // Legacy (still used for chat overlay)
  contactRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: colors.white, borderRadius: radii.md, marginBottom: 2, border: `1px solid ${colors.gray200}` },
  avatar: { width: 44, height: 44, borderRadius: '50%', background: colors.gray100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 600, color: colors.gray900, flexShrink: 0, border: '2px solid transparent' },
  avatarOnline: { borderColor: colors.greenDot },
  contactName: { fontSize: 15, fontWeight: 500, color: colors.gray900 },
  contactSub: { fontSize: 12, color: colors.gray400 },
  iconBtn: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', padding: 8, display: 'flex', alignItems: 'center' },
  textBtn: { background: 'none', border: 'none', color: colors.gray900, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: FONT, padding: 0 },
  badge: { background: colors.red, color: colors.white, fontSize: 11, fontWeight: 600, borderRadius: 10, padding: '2px 8px', minWidth: 20, textAlign: 'center' },

  // Forms
  formCard: { background: colors.white, borderRadius: radii.lg, padding: 16, display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12, border: `1px solid ${colors.gray200}` },
  input: { background: colors.gray50, border: `1px solid ${colors.gray200}`, borderRadius: radii.sm, padding: '12px 14px', color: colors.gray900, fontSize: 15, fontFamily: FONT },
  primaryBtn: { background: colors.gray900, border: 'none', color: colors.white, padding: '12px 20px', fontSize: 15, fontWeight: 500, cursor: 'pointer', borderRadius: radii.sm, fontFamily: FONT, whiteSpace: 'nowrap' },

  // Settings (More tab)
  settingsCard: { background: colors.white, borderRadius: radii.lg, overflow: 'hidden', marginBottom: 16, border: `1px solid ${colors.gray200}` },
  settingsRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: `1px solid ${colors.gray100}` },
  settingsLabel: { fontSize: 15, color: colors.gray900 },
  settingsValue: { fontSize: 15, color: colors.gray400 },
  settingsAction: { display: 'block', width: '100%', textAlign: 'left', padding: '14px 16px', background: 'none', border: 'none', fontSize: 15, color: colors.gray900, cursor: 'pointer', fontFamily: FONT },
  logoutBtn: { width: '100%', background: colors.white, border: `1px solid ${colors.gray200}`, borderRadius: radii.lg, padding: 16, fontSize: 15, fontWeight: 500, color: colors.red, cursor: 'pointer', fontFamily: FONT, textAlign: 'center', marginTop: 8 },

  // Tab Bar
  tabBar: { position: 'fixed', bottom: 0, left: 0, right: 0, display: 'flex', justifyContent: 'space-around', alignItems: 'center', background: 'rgba(21,20,26,0.9)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderTop: `1px solid ${colors.gray200}`, paddingTop: 6, paddingBottom: 'max(8px, env(safe-area-inset-bottom))', zIndex: 50 },
  tabBtn: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 12px', position: 'relative', fontFamily: FONT, minWidth: 56 },
  tabBtnActive: {},
  tabLabel: { fontSize: 10, fontWeight: 500, color: colors.gray400, lineHeight: 1 },
  tabLabelActive: { color: colors.gray900 },
  tabBadge: { position: 'absolute', top: -2, right: 2, background: colors.red, color: colors.white, fontSize: 9, fontWeight: 700, borderRadius: 10, padding: '1px 5px', minWidth: 14, textAlign: 'center' },
};

// ─── Active Call Bar Styles ───────────────────────────────────
export const activeCallBar = {
  bar: {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 90,
    background: `linear-gradient(135deg, #3a8a6a, #2f6f56)`,
    padding: 'max(6px, env(safe-area-inset-top)) 16px 6px',
    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: FONT,
  },
  dot: { width: 8, height: 8, borderRadius: '50%', background: colors.white, animation: 'miniPulse 1.5s infinite' },
  text: { flex: 1, color: colors.white, fontSize: 13, fontWeight: 600 },
  chevron: { color: 'rgba(255,255,255,0.7)', fontSize: 18 },
};
